import { Component, OnInit } from '@angular/core';
import { IktatgatoService } from '../iktatgato.service';
import { DataService } from '../../data.service';

/**
 *  Iratkezelési szabályok és jogosultság.
 *
 *  A szabálykészlet alapja a Másolatkészítési Szabályzat (200 dpi, PDF, záradékok),
 *  de MINDEN érték intézményenként átírható, és saját szabály is felvehető –
 *  a rendszer nem drótoz be egyetlen intézmény szabályzatát sem.
 */
@Component({
  selector: 'app-ikt-beallitasok',
  templateUrl: './beallitasok.component.html',
  styleUrls: ['../iktatgato-layout/iktatgato-layout.component.scss'],
})
export class IktBeallitasokComponent implements OnInit {
  nezet: 'szabalyok' | 'jogosultsag' | 'hivatalok' = 'szabalyok';

  szabalyok: any[] = [];
  csoportok: string[] = [];
  ujSzabaly: any = { kulcs: '', nev: '', ertek: '', csoport: 'egyedi', tipus: 'szoveg' };
  ujSzabalyForm = false;

  jog: any = { admin_mindent_lat: true, jogosultsagok: [] };
  ujJog: any = { alany: '', hatokor: 'konyv', hatokor_id: '', szint: 'olvas' };
  konyvek: any[] = [];

  /* ── Hivatali kapus szervezetek névsora ──
   *  A küldemény címzettjét a KRID vagy a rövid név azonosítja; a hivatalos névsort a
   *  NISZ egy PDF mellékleteként teszi közzé. A kiszolgáló éjszaka frissíti, itt pedig
   *  látszik az állapota, és kézzel is elindítható. */
  hivAllapot: any = null;
  hivFut = false;
  hivKeres = '';
  hivTalalatok: any[] = [];
  private hivIdozito: any = null;
  private hivKeresIdozito: any = null;

  async hivatalokBetolt() {
    try {
      this.hivAllapot = await this.ikt.hivatalokAllapot();
      this.hivFut = !!this.hivAllapot?.fut;
      //  Amíg fut, magunktól nézünk rá – a menet percekig tarthat (10 MB + 15 900 sor).
      if (this.hivFut && !this.hivIdozito) {
        this.hivIdozito = setInterval(async () => {
          await this.hivatalokBetolt();
          if (!this.hivFut) { clearInterval(this.hivIdozito); this.hivIdozito = null; }
        }, 4000);
      }
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Nem sikerült lekérni a névsor állapotát.', 'error');
    }
  }

  async hivatalokFrissit() {
    try {
      const r: any = await this.ikt.hivatalokSzinkron();
      this.hivFut = true;
      this.data.sendToast(r?.uzenet || 'A frissítés elindult.', 'success');
      await this.hivatalokBetolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A frissítés nem indult el.', 'error');
    }
  }

  //  Gépelés közben keresünk, de nem minden leütésre: a tábla 15 900 soros.
  hivKeresValt() {
    clearTimeout(this.hivKeresIdozito);
    this.hivKeresIdozito = setTimeout(async () => {
      const k = (this.hivKeres || '').trim();
      if (k.length < 2) { this.hivTalalatok = []; return; }
      try { this.hivTalalatok = (await this.ikt.hivatalKereses(k))?.talalatok || []; }
      catch { this.hivTalalatok = []; }
    }, 300);
  }

  /** „2026. 09. 24. 03:20" – a nyers ISO-alak itt senkinek nem mond semmit. */
  hivIdo(ertek: any) {
    if (!ertek) return '—';
    const d = new Date(ertek);
    return isNaN(d.getTime()) ? String(ertek) : d.toLocaleString('hu-HU');
  }
  hivAllapotSzoveg(a: string) {
    return ({ fut: 'folyamatban', kesz: 'frissítve', valtozatlan: 'változatlan', hiba: 'hiba' } as any)[a] || a || '—';
  }

  constructor(private ikt: IktatgatoService, private data: DataService) {}

  async ngOnInit() { await this.betolt(); }

  async nezetValt() { if (this.nezet === 'hivatalok' && !this.hivAllapot) await this.hivatalokBetolt(); }

  async betolt() {
    try {
      this.szabalyok = await this.ikt.szabalyok();
      this.csoportok = [...new Set(this.szabalyok.map((s) => s.csoport))];
      this.jog = await this.ikt.jogosultsagok();
      this.konyvek = await this.ikt.konyvek();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Nem sikerült betölteni a beállításokat.', 'error');
    }
  }

  szabalyokCsoportban(cs: string) { return this.szabalyok.filter((s) => s.csoport === cs); }

  csoportCim(cs: string): string {
    const nevek: Record<string, string> = {
      iktatas: 'Iktatás', erkeztetes: 'Érkeztetés', masolat: 'Másolatkészítés',
      irattar: 'Irattár', jogosultsag: 'Jogosultság', egyedi: 'Saját szabályok',
    };
    return nevek[cs] || cs;
  }

  async ment(s: any) {
    try {
      await this.ikt.szabalyMent(s);
      this.data.sendToast('Szabály mentve.');
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A mentés nem sikerült.', 'error');
    }
  }

  async ujSzabalyMent() {
    if (!this.ujSzabaly.kulcs?.trim() || !this.ujSzabaly.nev?.trim()) {
      this.data.sendToast('A kulcs és a név kötelező.', 'warning');
      return;
    }
    await this.ment(this.ujSzabaly);
    this.ujSzabalyForm = false;
    this.ujSzabaly = { kulcs: '', nev: '', ertek: '', csoport: 'egyedi', tipus: 'szoveg' };
    await this.betolt();
  }

  async jogAd() {
    if (!this.ujJog.alany?.trim()) { this.data.sendToast('Kinek adjuk a jogosultságot?', 'warning'); return; }
    try {
      await this.ikt.jogosultsagAd(this.ujJog);
      this.data.sendToast('Jogosultság megadva.');
      this.ujJog = { alany: '', hatokor: 'konyv', hatokor_id: '', szint: 'olvas' };
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A jogosultság megadása nem sikerült.', 'error');
    }
  }

  async jogVon(j: any) {
    if (!confirm('Visszavonod ezt a jogosultságot?')) return;
    try {
      await this.ikt.jogosultsagVon(j.id);
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A visszavonás nem sikerült.', 'error');
    }
  }
}
